import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { apiRequest } from "../../api/http";
import { Notice, PaginatedResponse } from "../../types/admin";

export type NoticeListQuery = {
  page?: number;
  page_size?: number;
};

type NoticePayload = {
  title: string;
  content: string;
};

type NoticesState = {
  list: Notice[];
  totalCount: number;
  page: number;
  pageSize: number;
  loadingList: boolean;
  listError: string | null;
  current: Notice | null;
  loadingCurrent: boolean;
  currentError: string | null;
};

const initialState: NoticesState = {
  list: [],
  totalCount: 0,
  page: 1,
  pageSize: 20,
  loadingList: false,
  listError: null,
  current: null,
  loadingCurrent: false,
  currentError: null,
};

const toQuery = (query: NoticeListQuery): string => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.set(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
};

export const fetchNotices = createAsyncThunk<
  PaginatedResponse<Notice>,
  NoticeListQuery,
  { rejectValue: string }
>("notices/fetchNotices", async (query, { rejectWithValue }) => {
  try {
    return await apiRequest<PaginatedResponse<Notice>>(`/admin/notices${toQuery(query)}`);
  } catch (error) {
    const message = error instanceof Error ? error.message : "공지사항 목록 조회에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const fetchNoticeDetail = createAsyncThunk<Notice, number, { rejectValue: string }>(
  "notices/fetchNoticeDetail",
  async (noticeId, { rejectWithValue }) => {
    try {
      return await apiRequest<Notice>(`/admin/notices/${noticeId}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "공지사항 상세 조회에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

export const createNotice = createAsyncThunk<Notice, NoticePayload, { rejectValue: string }>(
  "notices/createNotice",
  async (payload, { rejectWithValue }) => {
    try {
      return await apiRequest<Notice>("/admin/notices", {
        method: "POST",
        body: payload,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "공지사항 등록에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

export const updateNotice = createAsyncThunk<
  Notice,
  { noticeId: number; payload: Partial<NoticePayload> },
  { rejectValue: string }
>("notices/updateNotice", async ({ noticeId, payload }, { rejectWithValue }) => {
  try {
    return await apiRequest<Notice>(`/admin/notices/${noticeId}`, {
      method: "PATCH",
      body: payload,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "공지사항 수정에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const deleteNotice = createAsyncThunk<number, number, { rejectValue: string }>(
  "notices/deleteNotice",
  async (noticeId, { rejectWithValue }) => {
    try {
      await apiRequest<{ deleted: boolean; notice_id: number }>(`/admin/notices/${noticeId}`, {
        method: "DELETE",
      });
      return noticeId;
    } catch (error) {
      const message = error instanceof Error ? error.message : "공지사항 삭제에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

const noticesSlice = createSlice({
  name: "notices",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotices.pending, (state) => {
        state.loadingList = true;
        state.listError = null;
      })
      .addCase(fetchNotices.fulfilled, (state, action) => {
        state.loadingList = false;
        state.list = action.payload.items;
        state.totalCount = action.payload.total_count;
        state.page = action.payload.page;
        state.pageSize = action.payload.page_size;
      })
      .addCase(fetchNotices.rejected, (state, action) => {
        state.loadingList = false;
        state.listError = action.payload ?? "공지사항 목록 조회에 실패했습니다.";
      })
      .addCase(fetchNoticeDetail.pending, (state) => {
        state.loadingCurrent = true;
        state.currentError = null;
      })
      .addCase(fetchNoticeDetail.fulfilled, (state, action) => {
        state.loadingCurrent = false;
        state.current = action.payload;
      })
      .addCase(fetchNoticeDetail.rejected, (state, action) => {
        state.loadingCurrent = false;
        state.currentError = action.payload ?? "공지사항 상세 조회에 실패했습니다.";
      })
      .addCase(createNotice.fulfilled, (state, action) => {
        state.list = [action.payload, ...state.list];
      })
      .addCase(updateNotice.fulfilled, (state, action) => {
        state.list = state.list.map((notice) =>
          notice.notice_id === action.payload.notice_id ? action.payload : notice
        );
        state.current = action.payload;
      })
      .addCase(deleteNotice.fulfilled, (state, action) => {
        state.list = state.list.filter((notice) => notice.notice_id !== action.payload);
      });
  },
});

export default noticesSlice.reducer;
